// Economy Mode (peak management) schedule helpers, ported from the mobile app's
// utils/economyMode.js so both apps read and edit the schedule the same way.
//
// The schedule is a 24-hour ring cut into four slices. Slice n ends at
// EndMode{n} (15-minute intervals) and has tariff Mode{n}: 1 off-peak,
// 2 mid-peak, 3 peak. Mid-peak usually brackets the peak window, so a period can
// own two slices. peakSemantics.ts owns the send-side reordering.

import { detectSeason, Season } from './peakSemantics';

export type Settings = Record<string, any>;

/** EndMode values are 15-minute intervals; the ring works in minutes. */
export const intervalToMinutes = (interval: unknown) => (Number(interval) || 0) * 15;
export const minutesToInterval = (minutes: number) => Math.round((Number(minutes) || 0) / 15);

/** "7:00 AM" for a time in minutes since midnight. */
export const formatTime = (minutes: number) => {
    const snapped = (Math.round(minutes / 15) * 15) % 1440;
    const hours = Math.floor(snapped / 60);
    const mins = snapped % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const display = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${display}:${String(mins).padStart(2, '0')} ${period}`;
};

// ── Days ────────────────────────────────────────────────────────────────────
// Sunday first, as on mobile. Keys are the firmware's field names.

export const DAYS = [
    { key: 'Sun', label: 'SUN' },
    { key: 'Mon', label: 'MON' },
    { key: 'Tue', label: 'TUE' },
    { key: 'Wed', label: 'WED' },
    { key: 'Thu', label: 'THU' },
    { key: 'Fri', label: 'FRI' },
    { key: 'Sat', label: 'SAT' },
];

// ── Periods ─────────────────────────────────────────────────────────────────
// Off-peak carries no toggles: everything runs normally then.

export type PeriodKey = 'peak' | 'mid' | 'off';

export const PERIODS: { key: PeriodKey; mode: number; label: string; prefix: string | null }[] = [
    { key: 'peak', mode: 3, label: 'On-Peak', prefix: 'Peak' },
    { key: 'mid', mode: 2, label: 'Mid-Peak', prefix: 'MidPeak' },
    { key: 'off', mode: 1, label: 'Off-Peak', prefix: null },
];

export const ALL_RUN = 'All components run normally';
export const NOTHING_RUNS = 'No components will run';

/** The three switchable components. */
export const COMPONENTS = [
    { key: 'Filter', label: 'Filter' },
    { key: 'Heater', label: 'Heater' },
    { key: 'Ozone', label: 'Ozone' },
];

/** e.g. ('peak', 'Heater') → 'PeakHeater'. Null for off-peak, which has none. */
export const componentField = (periodKey: PeriodKey, componentKey: string) => {
    const period = PERIODS.find((p) => p.key === periodKey);
    return period?.prefix ? `${period.prefix}${componentKey}` : null;
};

// ── Defaults ────────────────────────────────────────────────────────────────
// The factory schedule as the firmware ships it: what "Revert to Default"
// restores. No days are enabled out of the box.

export const DEFAULT_SETTINGS: Settings = {
    Mode1: 1, Mode2: 2, Mode3: 3, Mode4: 2, Mode5: 1, Mode6: 1,
    EndMode1: 28, EndMode2: 44, EndMode3: 68, EndMode4: 76, EndMode5: 96, EndMode6: 96,
    Sun: false, Mon: false, Tue: false, Wed: false, Thu: false, Fri: false, Sat: false,
    PeakFilter: false, PeakHeater: false, PeakOzone: false,
    MidPeakFilter: true, MidPeakHeater: false, MidPeakOzone: true,
};

/** Every field this screen reads or writes. */
export const PEAK_FIELDS = [
    ...DAYS.map((d) => d.key),
    ...[1, 2, 3, 4, 5, 6].flatMap((n) => [`Mode${n}`, `EndMode${n}`]),
    ...PERIODS.filter((p) => p.prefix).flatMap((p) => COMPONENTS.map((c) => `${p.prefix}${c.key}`)),
];

/** The Economy Mode fields from the spa's settings, over the defaults. Day flags arrive as 0/1 or booleans. */
export const readPeakFields = (settings: Settings = {}): Settings => {
    const out: Settings = { ...DEFAULT_SETTINGS };
    PEAK_FIELDS.forEach((key) => {
        if (settings[key] === undefined) return;
        out[key] = DAYS.some((d) => d.key === key)
            ? settings[key] === 1 || settings[key] === true
            : settings[key];
    });
    return out;
};

/** Whether anything the screen edits differs between two settings objects. */
export const hasChanges = (a: Settings = {}, b: Settings = {}) =>
    PEAK_FIELDS.some((k) => a[k] !== b[k]);

// ── Reading the schedule ────────────────────────────────────────────────────

export interface Slice { index: number; mode: number; start: number; end: number; }

/** The four slices of the ring, in minutes. Slice 1 wraps midnight. */
export const slices = (settings: Settings = {}): Slice[] => {
    const ends = [1, 2, 3, 4].map((n) => Number(settings[`EndMode${n}`]) || 0);
    const modes = [1, 2, 3, 4].map((n) => Number(settings[`Mode${n}`]) || 1);
    return [0, 1, 2, 3].map((i) => ({
        index: i + 1,
        mode: modes[i],
        start: intervalToMinutes(ends[(i + 3) % 4]),
        end: intervalToMinutes(ends[i]),
    }));
};

/** "11:00 AM - 5:00 PM" for a slice. */
export const formatRange = (slice: Slice) => `${formatTime(slice.start)} - ${formatTime(slice.end)}`;

/** Each period's time ranges and what still runs during it. */
export const describePeriods = (settings: Settings = {}) => {
    const all = slices(settings);
    return PERIODS.map((period) => {
        const ranges = all.filter((s) => s.mode === period.mode);
        // A toggle ON means that component keeps running through the period.
        const components = period.prefix
            ? COMPONENTS.filter((c) => Number(settings[`${period.prefix}${c.key}`]) > 0)
            : [];
        return {
            ...period,
            ranges,
            components,
            summary: period.prefix
                ? components.map((c) => c.label).join(', ') || NOTHING_RUNS
                : ALL_RUN,
        };
    });
};

export { detectSeason };

/** Summer and winter differ only in which shoulder slices count as peak: swap the 2s and 3s. */
export const applySeason = (settings: Settings, season: Season): Settings => {
    if (detectSeason(settings) === season) return settings;
    const next = { ...settings };
    for (let n = 1; n <= 6; n += 1) {
        const mode = Number(next[`Mode${n}`]);
        if (mode === 2) next[`Mode${n}`] = 3;
        else if (mode === 3) next[`Mode${n}`] = 2;
    }
    return next;
};

// ── Editing the schedule ────────────────────────────────────────────────────

/** Drag snaps to half-hours; boundaries may not come closer than 15 minutes. */
export const SNAP_MINUTES = 30;
export const MIN_GAP_MINUTES = 15;

/** Whether `value` lies in the ring arc from `from` to `to`, going clockwise. */
export const withinRing = (value: number, from: number, to: number) => {
    const v = ((value % 1440) + 1440) % 1440;
    const a = ((from % 1440) + 1440) % 1440;
    const b = ((to % 1440) + 1440) % 1440;
    return a <= b ? v >= a && v <= b : v >= a || v <= b;
};

/**
 * Move one boundary, clamped so it cannot cross or touch its neighbours.
 * Returns the original object when the move isn't allowed: a slice collapsed to
 * nothing could never be dragged back open.
 */
export const moveBoundary = (settings: Settings, index: number, minutes: number): Settings => {
    if (!(index >= 1 && index <= 4)) return settings;

    const ends = [1, 2, 3, 4].map((i) => intervalToMinutes(settings[`EndMode${i}`]));
    const prev = ends[(index + 2) % 4];
    const next = ends[index % 4];

    const snapped = Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
    const lower = (prev + MIN_GAP_MINUTES) % 1440;
    const upper = (next - MIN_GAP_MINUTES + 1440) % 1440;
    if (!withinRing(snapped, lower, upper)) return settings;

    const interval = minutesToInterval(snapped % 1440);
    if (interval === Number(settings[`EndMode${index}`])) return settings;
    return { ...settings, [`EndMode${index}`]: interval };
};
